import React, { useEffect, useRef, useState } from 'react';
import InputPergunta from '../components/analiseNegocialMetodologica/inputPergunta/InputPergunta';
import InputGroupPergunta from '../components/analiseNegocialMetodologica/inputGroupPergunta/InputGroupPergunta';
import LayoutAnalise, { layoutAnaliseType } from '../components/layoutAnalise/LayoutAnalise';
import styles from './AnaliseNegocialForm.module.scss';
import Pages from '../../../config/Pages';
import { useHistory, useParams } from 'react-router-dom';
import { usePerguntas } from '../components/analiseNegocialMetodologica/Hooks';
import AnaliseNegocialService from '../../../service/AnaliseNegocialService';
import { errosTratados } from '../../../config/Axios';
import { printToast } from '../../../core/utils/Global';
import { arrayToDateString } from '../../../core/utils/DateMixins';
import Shareds from '../../../config/translations/Shareds';
import PageMessages from '../../../config/translations/PageMessages';
import ConfirmDialog from '../../../core/components/ConfirmDialog';

function AnaliseNegocialForm() {
    const perguntas = usePerguntas(layoutAnaliseType.analiseNegocial, true);
    const inputsRef = useRef({});
    const parecerAnaliseNegocialRef = useRef(null);
    const history = useHistory();
    const { id } = useParams();
    const [proposta, setProposta] = useState(null);
    const confirmRef = useRef(null);

    const loadData = async () => {
        const response = await AnaliseNegocialService.buscarPorId({ id });
        const statusAtual =
            (response?.proposta?.propostaStatus ?? []).find((status) => status?.flagAtual) ?? {};
        parecerAnaliseNegocialRef.current.setTextValue(response?.descParecerNegocial);
        parecerAnaliseNegocialRef.current.setRadioValue(response?.parecer);
        for (let i = 0; i < response?.respostasAvaliacaoNegocial?.length; i++) {
            const resposta = response?.respostasAvaliacaoNegocial[i];
            inputsRef.current[resposta.propriedadeAvaliacao.id].setRadioValue(
                resposta.flagResposta
            );
            inputsRef.current[resposta.propriedadeAvaliacao.id].setTextValue(
                resposta.descObservacao ?? ''
            );
        }
        setProposta({
            numeroProposta: response?.proposta?.id,
            nmIndicador: response?.proposta?.nmIndicador,
            situacaoProposta: response?.proposta?.situacaoProposta?.descSituacao,
            finalizada: response?.situacaoAvaliacao.codgSituacaoAvaliacao === 'ANALISE_FINALIZADA',
            responsavelAnalise: response?.responsavelAnalise?.nomePessoa,
            dataCadastro: arrayToDateString(response?.dataCadastro, true),
            situacaoAvaliacao: response?.situacaoAvaliacao?.descSituacaoAvaliacao,
            codgTipoProposta: response?.proposta?.tipoProposta?.codgTipoProposta,
            file: statusAtual?.arquivo,
        });
    };

    const getValues = () => {
        const respostasAvaliacaoNegocial = Object.keys(inputsRef.current).reduce(
            (prev, curr) => [
                ...prev,
                {
                    descObservacao: inputsRef.current[curr]?.text,
                    propriedadeAvaliacao: { id: curr },
                    flagResposta: inputsRef.current[curr]?.radio,
                },
            ],
            []
        );
        return {
            respostasAvaliacaoNegocial,
            descParecerNegocial: parecerAnaliseNegocialRef.current.text,
            parecer: parecerAnaliseNegocialRef.current.radio,
        };
    };

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            <ConfirmDialog ref={confirmRef} />
            <LayoutAnalise
                disableSalvar={proposta?.finalizada}
                proposta={proposta}
                classNameChildrenContainer={styles.containerGroup}
                onVoltar={() => history.push(Pages.sei.analiseNegocial.url.list)}
                onSalvar={async () => {
                    try {
                        await AnaliseNegocialService.salvar(id, getValues());
                        printToast({
                            severity: 'success',
                            summary: Pages.sei.analiseNegocial.title,
                            detail: Shareds.form.successUpdate,
                        });
                        loadData();
                    } catch (error) {
                        if (!errosTratados.includes(error.response?.status)) {
                            printToast({
                                severity: 'error',
                                summary: Pages.sei.analiseNegocial.title,
                                detail: error.response?.message,
                            });
                        }
                    }
                }}
                onFinalizar={async () =>
                    confirmRef.current?.show(
                        PageMessages.sei.analiseNegocial.confirmFinnaly,
                        async () => {
                            const dados = getValues();
                            try {
                                await AnaliseNegocialService.finalizar(id, dados);
                                confirmRef.current.hide();
                                printToast({
                                    severity: 'success',
                                    summary: Pages.sei.analiseNegocial.title,
                                    detail: Shareds.form.successUpdate,
                                });
                                loadData();
                            } catch (error) {
                                if (!errosTratados.includes(error.response?.status)) {
                                    printToast({
                                        severity: 'error',
                                        summary: Pages.sei.analiseNegocial.title,
                                        detail: error.response?.message,
                                    });
                                }
                            }
                        }
                    )
                }
            >
                {Object.keys(perguntas).map((pergunta, index) => (
                    <InputGroupPergunta
                        label={`${index + 1}.0 - ${perguntas[
                            pergunta
                        ][0].grupoPropriedadeAvaliacao?.descGrupoPropriedadeAva.toUpperCase()}`}
                        key={index}
                    >
                        <div className={`${styles.painelPergunta}`}>
                            {perguntas?.[pergunta]?.map(
                                ({ id, descPropriedadeAvaliacao }, index) => (
                                    <InputPergunta
                                        key={index}
                                        descricao={descPropriedadeAvaliacao}
                                        ref={(el) => (inputsRef.current[id] = el)}
                                        disabled={proposta?.finalizada}
                                    />
                                )
                            )}
                        </div>
                    </InputGroupPergunta>
                ))}

                <InputGroupPergunta label={'5.0 - PARECER NEGOCIAL DO INDICADOR'}>
                    <InputPergunta
                        descricao={
                            '5.1 - Na análise do Indicador, verificou-se que o mesmo está APTO ou INAPTO para embasar decisões, seja no nível operacional, tático ou estratégico?'
                        }
                        ref={parecerAnaliseNegocialRef}
                        vertical
                        placeholder={'Descreva o seu Parecer Negocial do Indicador...'}
                        disabled={proposta?.finalizada}
                        maxLen={3500}
                    />
                </InputGroupPergunta>
            </LayoutAnalise>
        </>
    );
}

export default AnaliseNegocialForm;
